/**
 * home.ts — The walker's own plot: where the house stands on it, and where the
 * walk begins.
 *
 * A level says where home is as a single rect (`spec.home`), and that rect is
 * two different things at once: the **win zone** the player has to get back
 * inside, and the **plot** their house stands on. This module is the one place
 * that decides how the second is arranged inside the first.
 *
 * ## Why it isn't just "the middle of the pad"
 *
 * It used to be. The start point was the centre of the home rect and the house
 * was drawn centred on the same point — so the walker began the run standing
 * *inside* their own front room, and the only thing hiding it was that the
 * house wasn't drawn until the last objective flipped. Which is its own bug:
 * you set off from a bare grey square and a cottage appeared behind you twenty
 * seconds later.
 *
 * So the plot is laid out the way a real one is. The house sits at the back of
 * it, its front door faces south (the way the camera looks from), and the walk
 * starts on the doormat in front of that door with the garden behind. Home is
 * then something you can *see* for the whole walk, and coming back to it means
 * walking up to a house rather than onto a rectangle.
 *
 * ## Why both callers ask here rather than working it out
 *
 * Three places need the same answer and would drift the moment they each
 * computed it: the generator and `config/level.ts` (where does the walker
 * start?), the renderer (where does the cottage go?), and `Game` plus the
 * validator (what is solid?). A house drawn a few units off the rect it
 * collides on is a wall you can stand in, which is exactly the class of bug
 * `systems/terrace.ts` and `systems/roofline.ts` exist to keep out of the
 * render layer.
 */
import { OBJECTIVE } from '../config/constants';
import { rectCenter, type Rect } from './level';
import type { Vec2 } from '../utils/vec2';

/**
 * The house's footprint on the plot — centred east–west, flush with the plot's
 * **north** edge.
 *
 * Flush at the back rather than inset, because every unit spent behind the
 * house is a unit the front garden doesn't get, and the garden is the part the
 * player actually stands in. This is the rect the walls are drawn on *and* the
 * rect the walker collides with; there is deliberately only one.
 */
export function homeHouseRect(home: Rect): Rect {
  const { WIDTH, DEPTH } = OBJECTIVE.COTTAGE;
  const [cx] = rectCenter(home);
  return { x: cx - WIDTH / 2, y: home.y, w: WIDTH, h: DEPTH };
}

/**
 * The doormat — where the walk starts, and the spot the "go home" marker is
 * pinned to.
 *
 * Squarely in front of the door: on the house's centreline, `DOORSTEP` clear of
 * its front wall. Well inside the plot, so a walker standing here has already
 * won when they arrive back on it.
 */
export function homeDoorstep(home: Rect): Vec2 {
  const house = homeHouseRect(home);
  const [cx] = rectCenter(home);
  return { x: cx, y: house.y + house.h + OBJECTIVE.COTTAGE.DOORSTEP };
}
